import type {
  BookingSchema,
  MoveBookingSchema,
  UpdateBookingSchema,
  UpdateBookingStatusSchema,
} from "@repo/validators";
import type { z } from "zod";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import {
  addMonths,
  endOfMonth,
  startOfDay,
  startOfMonth,
  subDays,
} from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";

import type { PartialBooking } from "~/providers/booking-provider";
import type { ManagerResult } from "~/types";
import { useBooking } from "~/hooks/use-booking";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";
import { useAlertStore } from "~/stores/use-alert-store";
import { useTempBookingStore } from "~/stores/use-booking-store";
import { usePermissionManager } from "./use-permission-manager";

export const useBookingManager = () => {
  const utils = api.useUtils();

  const { bookings } = useBooking();

  const { trialEnded, user } = useUser();

  const { handleBookingUpdate, handleBookingDelete } = useBookingUpdates();

  const {
    setBookingDialogOpen,
    setSelectedBooking,
    setTempBooking,
    selectedDate,
    setPendingBookingsOpen,
  } = useTempBookingStore();

  const { hasPermission } = usePermissionManager();

  const { t } = useTranslation("common");

  const { showDialog } = useAlertStore(
    useShallow((state) => ({
      showDialog: state.showDialog,
    })),
  );

  const { mutate: acceptBooking } = api.booking.acceptBooking.useMutation({
    async onSuccess(_, id) {
      toast.success("Booking accepted!");
      await handleBookingUpdate(id);
    },
  });

  const { mutate: sendMessageToGuest } =
    api.booking.sendMessageToGuest.useMutation({
      onSuccess() {
        toast.success("Message sent!");
      },
      onError() {
        toast.error("Could not send message");
      },
    });

  const { mutate: declineBooking } = api.booking.declineBooking.useMutation({
    async onSuccess(_, id) {
      toast.success("Booking declined");
      await handleBookingUpdate(id);
    },
  });

  //TODO: change labels to labelids
  const { mutate: createBooking, isPending: isCreatingBooking } =
    api.booking.create.useMutation({
      async onMutate(newBooking) {
        await utils.booking.getAll.cancel({
          startDate: startOfMonth(selectedDate),
          endDate: endOfMonth(addMonths(selectedDate, 2)),
        });

        const prevData = utils.booking.getAll.getData({
          startDate: startOfMonth(selectedDate),
          endDate: endOfMonth(addMonths(selectedDate, 2)),
        });

        utils.booking.getAll.setData(
          {
            startDate: startOfMonth(selectedDate),
            endDate: endOfMonth(addMonths(selectedDate, 2)),
          },
          (old) => {
            if (!old) return prevData;
            return [
              ...old,
              {
                ...newBooking,
                createdAt: new Date(),
                phone: newBooking.phone ?? "",
                firstName: newBooking.firstName ?? "",
                fullName: newBooking.firstName + " " + newBooking.lastName,
                lastName: newBooking.lastName ?? "",
                email: newBooking.email ?? "",
                comment: newBooking.comment ?? "",
                memo: newBooking.memo ?? "",
                createdBy: newBooking.createdBy,
                labels: [],
                audits: [],
                starred: newBooking.starred ?? false,
              },
            ];
          },
        );

        toast.success(t("create_item_success", { item: t("Booking.booking") }));

        return { prevData };
      },

      async onSuccess(result) {
        await handleBookingUpdate(result);
      },

      onError: () => {
        toast.error(t("create_fail_item", { item: t("Booking.booking") }));
      },
    });

  const { mutate: updateBooking, isPending: isUpdatingBooking } =
    api.booking.update.useMutation({
      async onMutate(updatedBooking) {
        await utils.booking.getAll.cancel({
          startDate: startOfMonth(selectedDate),
          endDate: endOfMonth(addMonths(selectedDate, 2)),
        });

        const prevData = utils.booking.getAll.getData({
          startDate: startOfMonth(selectedDate),
          endDate: endOfMonth(addMonths(selectedDate, 2)),
        });

        utils.booking.getAll.setData(
          {
            startDate: startOfMonth(selectedDate),
            endDate: endOfMonth(addMonths(selectedDate, 2)),
          },
          (old) => {
            if (!old) return prevData;

            const previousBooking = old.find(
              (booking) => booking.id === updatedBooking.id,
            );

            if (!previousBooking) {
              return prevData;
            }

            return [
              ...old.filter((booking) => booking.id !== previousBooking.id),
              {
                ...updatedBooking,
                createdAt: previousBooking.createdAt,
                phone: updatedBooking.phone ?? previousBooking.phone,
                firstName:
                  updatedBooking.firstName ?? previousBooking.firstName,
                lastName: updatedBooking.lastName ?? previousBooking.lastName,
                fullName:
                  updatedBooking.firstName + " " + updatedBooking.lastName,
                email: updatedBooking.email ?? previousBooking.email,
                comment: updatedBooking.comment ?? previousBooking.comment,
                memo: updatedBooking.memo ?? previousBooking.memo,
                status: updatedBooking.status ?? previousBooking.status,
                bookingStatus:
                  updatedBooking.bookingStatus ?? previousBooking.bookingStatus,
                createdBy: previousBooking.createdBy,
                provider: previousBooking.provider,
                startTime:
                  updatedBooking.startTime ?? previousBooking.startTime,
                endTime: updatedBooking.endTime ?? previousBooking.endTime,
                groupSize:
                  updatedBooking.groupSize ?? previousBooking.groupSize,
                tableIds: updatedBooking.tableIds ?? previousBooking.tableIds,
                labels: previousBooking.labels,
                audits: previousBooking.audits,
                starred: updatedBooking.starred ?? previousBooking.starred,
              },
            ];
          },
        );

        toast.success(t("update_item_success", { item: t("Booking.booking") }));

        return { prevData };
      },
      async onSuccess(_, variables) {
        await handleBookingUpdate(variables.id);
      },
    });

  const { mutate: deleteBooking, isPending: isDeletingBooking } =
    api.booking.delete.useMutation({
      onMutate: async (variables) => {
        await utils.booking.getAll.cancel({
          startDate: startOfMonth(selectedDate),
          endDate: endOfMonth(addMonths(selectedDate, 2)),
        });

        const prevData = utils.booking.getAll.getData({
          startDate: startOfMonth(selectedDate),
          endDate: endOfMonth(addMonths(selectedDate, 2)),
        });

        utils.booking.getAll.setData(
          {
            startDate: startOfMonth(selectedDate),
            endDate: endOfMonth(addMonths(selectedDate, 2)),
          },
          (old) => {
            if (!old) return prevData;
            return old.filter((p) => p.id !== variables);
          },
        );
      },
      onSuccess(_, variables) {
        handleBookingDelete(variables);
        toast.success(
          capitalize(t("delete_item_success", { item: t("Booking.booking") })),
        );
      },
      onError: () => {
        toast.error(
          capitalize(t("delete_item_fail", { item: t("Booking.booking") })),
        );
      },
    });

  const { mutate: updateBookingStatus } =
    api.booking.updateBookingStatus.useMutation({
      async onMutate(updatedBooking) {
        await utils.booking.getAll.cancel({
          startDate: startOfMonth(selectedDate),
          endDate: endOfMonth(addMonths(selectedDate, 2)),
        });
        toast.success(
          capitalize(
            t("update_item_success", { item: t("Booking.booking_status") }),
          ),
        );
        const prevData = utils.booking.getAll.getData({
          startDate: startOfMonth(selectedDate),
          endDate: endOfMonth(addMonths(selectedDate, 2)),
        });

        utils.booking.getAll.setData(
          {
            startDate: startOfMonth(selectedDate),
            endDate: endOfMonth(addMonths(selectedDate, 2)),
          },
          (old) => {
            if (!old) return prevData;

            const previousBooking = old.find(
              (booking) => booking.id === updatedBooking.id,
            );

            if (!previousBooking) {
              return prevData;
            }

            return [
              ...old.filter((booking) => booking.id !== previousBooking.id),
              {
                ...previousBooking,
                bookingStatus: updatedBooking.bookingStatus,
              },
            ];
          },
        );

        return { prevData };
      },
      async onSuccess(_, variables) {
        await handleBookingUpdate(variables.id);
      },
    });

  const { mutate: moveTable } = api.booking.moveTable.useMutation({
    async onMutate(updatedBooking) {
      await utils.booking.getAll.cancel({
        startDate: startOfMonth(selectedDate),
        endDate: endOfMonth(addMonths(selectedDate, 2)),
      });

      const prevData = utils.booking.getAll.getData({
        startDate: startOfMonth(selectedDate),
        endDate: endOfMonth(addMonths(selectedDate, 2)),
      });

      utils.booking.getAll.setData(
        {
          startDate: startOfMonth(selectedDate),
          endDate: endOfMonth(addMonths(selectedDate, 2)),
        },
        (old) => {
          if (!old) return prevData;

          const previousBooking = old.find(
            (booking) => booking.id === updatedBooking.id,
          );

          if (!previousBooking) {
            return prevData;
          }

          return [
            ...old.filter((booking) => booking.id !== previousBooking.id),
            {
              ...previousBooking,
              tableIds: updatedBooking.tableIds,
            },
          ];
        },
      );

      toast.success(t("update_item_success", { item: t("Booking.booking") }));

      return { prevData };
    },
  });

  const isLoading = isCreatingBooking || isUpdatingBooking || isDeletingBooking;

  const createBookingHandler = async (data: z.infer<typeof BookingSchema>) => {
    if (trialEnded) {
      await showDialog({
        title: t("trial_ended_title"),
        message: t("trial_ended_message"),
        buttons: [
          {
            label: t("ok"),
            value: "ok",
            variant: "default",
          },
        ],
      });
      return null;
    }
    const { permission, employee } = await hasPermission({
      requiredPermission: Permissions.create_booking,
      label: t("Permissions.create_booking_label"),
    });

    if (!permission) return null;

    createBooking({
      ...data,
      createdBy: employee?.id,
      startTime: fromZonedTime(data.startTime, user.timeZone),
      endTime: fromZonedTime(data.endTime, user.timeZone),
    });

    return true;
  };

  const updateBookingHandler = async (
    data: z.infer<typeof UpdateBookingSchema>,
  ) => {
    const { permission, employee } = await hasPermission({
      requiredPermission: Permissions.update_booking,
      label: t("Permissions.update_booking_label"),
    });

    const zonedStartTime = data.startTime
      ? fromZonedTime(data.startTime, user.timeZone)
      : undefined;
    const zonedEndTime = data.endTime
      ? fromZonedTime(data.endTime, user.timeZone)
      : undefined;

    if (!permission) return null;

    updateBooking({
      ...data,
      updatedBy: employee?.id,
      startTime: zonedStartTime,
      endTime: zonedEndTime,
    });

    return true;
  };

  const deleteBookingHandler = async (bookingId: string) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.delete_booking,
      label: t("Permissions.delete_booking_label"),
    });

    if (!permission) return null;

    const result = await showDialog({
      title: t("before_delete_booking_title"),
      message: t("before_delete_booking_message"),
      buttons: [
        {
          label: t("cancel"),
          value: "cancel",
          variant: "default",
        },
        {
          label: t("delete"),
          value: "ok",
          variant: "destructive",
        },
      ],
    });

    if (result === "cancel") return null;

    return await new Promise<boolean>((resolve) => {
      deleteBooking(bookingId, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const moveBookingHandler = async (
    data: z.infer<typeof MoveBookingSchema>,
  ) => {
    const bookingToUpdate = bookings.find((b) => b.id === data.id);

    if (!bookingToUpdate) return null;

    if (bookingToUpdate.tableIds.toString() === data.tableIds.toString())
      return false;

    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_booking,
      label: t("Permissions.update_booking_label"),
    });

    if (!permission) return null;

    moveTable(data);

    return true;
  };

  const bookingStatusUpdateHandler = async (
    data: z.infer<typeof UpdateBookingStatusSchema>,
  ) => {
    const { permission, employee } = await hasPermission({
      requiredPermission: Permissions.update_booking,
      label: t("Permissions.update_booking_label"),
    });

    return new Promise<
      ManagerResult<keyof z.infer<typeof UpdateBookingStatusSchema>>
    >((resolve) => {
      if (!permission)
        return resolve({
          error: { path: "root", message: t("Permissions.no_permission") },
        });
      updateBookingStatus(
        {
          ...data,
          employeeId: employee?.id,
        },
        {
          onSuccess() {
            return resolve({
              data: true,
            });
          },
          onError() {
            resolve({
              error: {
                path: "root",
                message: capitalize(
                  t("update_item_error", {
                    item: t("Booking.booking_status"),
                  }),
                ),
              },
            });
          },
        },
      );
    });
  };

  const createTempBooking = async (booking: PartialBooking) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_booking,
      label: t("Permissions.create_booking_label"),
    });

    if (!permission) return null;

    setTempBooking(booking);

    return true;
  };

  const openBookingDialog = async (booking?: PartialBooking) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_booking,
      label: t("Permissions.create_booking_label"),
    });

    if (!permission) return null;
    setSelectedBooking(booking);
    setBookingDialogOpen(true);
  };

  const openBookingById = async (bookingId: string) => {
    // First, check if the booking is cached
    let booking = bookings.find((b) => b.id === bookingId);

    if (!booking) {
      try {
        // Add a small delay before fetching
        console.log("START DELAY");

        console.log("END DELAY");

        // Attempt to fetch the booking if not cached
        booking = await utils.booking.getById.fetch(bookingId);
      } catch (error) {
        alert("could not open booking");
        console.error(error);
        return; // Exit if the fetch fails
      }
    }

    // Proceed with the booking if it exists

    await openBookingDialog(booking);
  };

  const acceptBookingHandler = async (bookingId: string) => {
    return new Promise<boolean>((resolve) => {
      acceptBooking(bookingId, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const sendMessageToGuestHandler = async (
    bookingId: string,
    email: string,
    message: string,
  ) => {
    return new Promise<boolean>((resolve) => {
      sendMessageToGuest(
        { bookingId, email, message },
        {
          async onSuccess() {
            await handleBookingUpdate(bookingId);
            resolve(true);
          },
          onError() {
            resolve(false);
          },
        },
      );
    });
  };

  const declineBookingHandler = async (bookingId: string) => {
    return new Promise<boolean>((resolve) => {
      declineBooking(bookingId, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const openPendingBookings = () => {
    setPendingBookingsOpen(true);
  };

  const closeBookingDialog = () => {
    // check unsaved changes
    setBookingDialogOpen(false);
    setSelectedBooking(undefined);
  };

  return {
    acceptBooking: acceptBookingHandler,
    declineBooking: declineBookingHandler,
    sendMessageToGuest: sendMessageToGuestHandler,
    openPendingBookings,
    createBooking: createBookingHandler,
    updateBooking: updateBookingHandler,
    deleteBooking: deleteBookingHandler,
    moveBooking: moveBookingHandler,
    updateStatus: bookingStatusUpdateHandler,
    openBookingDialog,
    openBookingById,
    closeBookingDialog,
    createTempBooking,
    isLoading,
  };
};

export const useBookingUpdates = () => {
  const utils = api.useUtils();

  const getSelectedDate = useTempBookingStore((state) => state.getSelectedDate);

  const handleBookingUpdate = async (bookingId: string) => {
    const result = await utils.booking.getById.fetch(bookingId);
    const selectedDate = getSelectedDate();

    void utils.booking.getAll.setData(
      {
        startDate: startOfMonth(selectedDate),
        endDate: endOfMonth(addMonths(selectedDate, 2)),
      },
      (old) => {
        if (!old) return old;
        return [...old.filter((p) => p.id !== result.id), result];
      },
    );
  };

  const handleBookingDelete = (bookingId: string) => {
    const selectedDate = getSelectedDate();

    void utils.booking.getAll.setData(
      {
        startDate: startOfMonth(selectedDate),
        endDate: endOfMonth(addMonths(selectedDate, 2)),
      },
      (old) => {
        if (!old) return old;
        return old.filter((p) => p.id !== bookingId);
      },
    );
  };

  return {
    handleBookingUpdate,
    handleBookingDelete,
  };
};
